/**
 * HOVER UPLOAD COMPONENT
 * 
 * Upload and process Hover Property Reports for exterior measurements
 * Shows roof, siding, windows, gutters, and trim measurements
 */

import React, { useState, useRef } from 'react';
import { 
  Home, 
  Upload, 
  FileText, 
  Check, 
  AlertCircle, 
  Loader2, 
  X,
  ChevronDown,
  ChevronUp,
  Ruler,
  Grid,
  Square,
  Wind,
  Droplets
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const HoverUpload = ({ 
  disabled = false, 
  fieldVisitId,
  onImportComplete,
}) => {
  const fileInputRef = useRef(null);
  const [status, setStatus] = useState('idle'); // idle, uploading, reviewing, confirming, complete, error
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [parsedData, setParsedData] = useState(null);
  const [importId, setImportId] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    roof: true,
    siding: true,
    windows: false,
    gutters: false,
  });

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validExtensions = ['.pdf', '.json'];
    const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!validExtensions.includes(fileExt)) {
      setError('Please select a PDF or JSON file from Hover export.');
      return;
    }

    // Validate file size (max 20MB)
    if (file.size > 20 * 1024 * 1024) {
      setError('File is too large. Maximum size is 20MB.');
      return;
    }

    setStatus('uploading');
    setError(null);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const response = await fetch(
        `${API_BASE}/api/field-visits/${fieldVisitId}/hover/upload`,
        {
          method: 'POST',
          credentials: 'include',
          body: formData,
        }
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to upload file');
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to parse Hover report');
      }

      setParsedData(data);
      setImportId(data.import_id);
      setStatus('reviewing');

    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload file');
      setStatus('error');
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleConfirmImport = async () => {
    if (!importId) return;

    setStatus('confirming');
    setError(null);

    try {
      const response = await fetch(
        `${API_BASE}/api/field-visits/${fieldVisitId}/hover/confirm`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            import_id: importId,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to confirm import');
      }

      const result = await response.json();
      
      setStatus('complete');
      
      // Notify parent component
      if (onImportComplete) {
        onImportComplete({
          areas_imported: result.areas_imported,
          exterior_data: result.exterior_data,
        });
      }

    } catch (err) {
      console.error('Confirm error:', err);
      setError(err.message || 'Failed to confirm import');
      setStatus('error');
    }
  };

  const handleCancel = () => {
    setStatus('idle');
    setError(null);
    setParsedData(null);
    setImportId(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Format square feet with commas
  const formatSF = (value) => {
    if (!value) return '0';
    return Math.round(value).toLocaleString();
  };

  // Render based on status
  if (status === 'idle' || status === 'error') {
    return (
      <div className="p-6 border-2 border-dashed border-slate-700 rounded-lg bg-slate-800/30">
        <div className="text-center">
          <Home className="w-12 h-12 text-blue-500 mx-auto mb-3" />
          <h4 className="text-lg font-medium text-slate-200 mb-2">Hover Exterior Import</h4>
          <p className="text-sm text-slate-400 mb-4">
            Upload a Hover Property Report for exterior measurements (roof, siding, windows)
          </p>
          
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded text-left">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-400">{error}</p>
              </div>
            </div>
          )}
          
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.json"
            onChange={handleFileSelect}
            disabled={disabled || !fieldVisitId}
            className="hidden"
            data-testid="hover-file-input"
          />
          
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || !fieldVisitId}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded 
              flex items-center gap-2 mx-auto disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="hover-upload-button"
          >
            <Upload className="w-4 h-4" />
            Upload Report
          </button>
          
          <p className="text-xs text-slate-500 mt-3">
            Accepts: .pdf, .json files from Hover export
          </p>
        </div>
      </div>
    );
  }

  if (status === 'uploading') {
    return (
      <div className="p-6 border-2 border-slate-700 rounded-lg bg-slate-800/30">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-500 mx-auto mb-3 animate-spin" />
          <h4 className="text-lg font-medium text-slate-200 mb-2">Processing Report...</h4>
          <p className="text-sm text-slate-400 mb-4">
            Extracting exterior measurements
          </p>
          
          {/* Progress bar */}
          <div className="max-w-xs mx-auto">
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-500 transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <p className="text-xs text-slate-500 mt-2">{uploadProgress}%</p>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'reviewing' && parsedData) {
    const preview = parsedData.preview || {};
    const overview = parsedData.property_overview || {};
    const areas = preview.areas || [];
    const windows = preview.windows || {};
    const doors = preview.doors || {};
    const gutters = preview.gutters || {};
    const trim = preview.trim || {};

    return (
      <div className="border-2 border-blue-500/30 rounded-lg bg-slate-800/50 overflow-hidden">
        {/* Header */}
        <div className="p-4 bg-blue-500/10 border-b border-blue-500/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="w-6 h-6 text-blue-400" />
              <div>
                <h4 className="text-lg font-medium text-slate-200">Hover Import Preview</h4>
                <p className="text-sm text-slate-400">
                  {parsedData.property_address || 'Exterior measurements ready for import'}
                </p>
              </div>
            </div>
            <button
              onClick={handleCancel}
              className="p-2 hover:bg-slate-700 rounded"
              title="Cancel import"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Warnings */}
        {parsedData.warnings?.length > 0 && (
          <div className="p-3 bg-amber-500/10 border-b border-amber-500/30">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5" />
              <div className="text-sm text-amber-400">
                {parsedData.warnings.map((w, i) => (
                  <p key={i}>{w}</p>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Overview Stats */}
        <div className="p-4 border-b border-slate-700 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-slate-200">{formatSF(overview.total_roof_sf)}</div>
            <div className="text-xs text-slate-500">Roof SF</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-slate-200">{formatSF(overview.total_siding_sf)}</div>
            <div className="text-xs text-slate-500">Siding SF</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-slate-200">{overview.total_window_count || 0}</div>
            <div className="text-xs text-slate-500">Windows</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-slate-200">{overview.total_door_count || 0}</div>
            <div className="text-xs text-slate-500">Doors</div>
          </div>
        </div>

        {/* Collapsible Sections */}
        <div className="p-4 max-h-80 overflow-y-auto space-y-3">
          
          {/* Roof Section */}
          {areas.some(a => a.type === 'roof') && (
            <div className="border border-slate-700 rounded overflow-hidden">
              <button
                onClick={() => toggleSection('roof')}
                className="w-full p-3 flex items-center justify-between hover:bg-slate-800/50 bg-slate-800/30"
              >
                <div className="flex items-center gap-3">
                  <Grid className="w-5 h-5 text-amber-400" />
                  <span className="font-medium text-slate-200">Roof</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-slate-400">
                    {formatSF(overview.total_roof_sf)} SF
                  </span>
                  {expandedSections.roof ? (
                    <ChevronUp className="w-4 h-4 text-slate-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                  )}
                </div>
              </button>
              
              {expandedSections.roof && (
                <div className="p-3 border-t border-slate-700 bg-slate-800/20">
                  {areas.filter(a => a.type === 'roof').map(area => (
                    <div key={area.area_id} className="flex items-center justify-between py-1">
                      <span className="text-slate-300">{area.label}</span>
                      <div className="text-sm text-slate-400">
                        {formatSF(area.dimensions?.floor_sf)} SF
                        {area.dimensions?.pitch && ` • ${area.dimensions.pitch}/12 pitch`}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Siding Section */}
          {areas.some(a => a.type === 'exterior') && (
            <div className="border border-slate-700 rounded overflow-hidden">
              <button
                onClick={() => toggleSection('siding')}
                className="w-full p-3 flex items-center justify-between hover:bg-slate-800/50 bg-slate-800/30"
              >
                <div className="flex items-center gap-3">
                  <Square className="w-5 h-5 text-blue-400" />
                  <span className="font-medium text-slate-200">Siding / Facades</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-slate-400">
                    {formatSF(overview.total_siding_sf)} SF
                  </span>
                  {expandedSections.siding ? (
                    <ChevronUp className="w-4 h-4 text-slate-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                  )}
                </div>
              </button>
              
              {expandedSections.siding && (
                <div className="p-3 border-t border-slate-700 bg-slate-800/20">
                  {areas.filter(a => a.type === 'exterior').map(area => (
                    <div key={area.area_id} className="flex items-center justify-between py-1">
                      <span className="text-slate-300">{area.label}</span>
                      <span className="text-sm text-slate-400">
                        {formatSF(area.dimensions?.wall_sf)} SF
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Windows & Doors Section */}
          {(windows.count > 0 || doors.count > 0) && (
            <div className="border border-slate-700 rounded overflow-hidden">
              <button
                onClick={() => toggleSection('windows')}
                className="w-full p-3 flex items-center justify-between hover:bg-slate-800/50 bg-slate-800/30"
              >
                <div className="flex items-center gap-3">
                  <Wind className="w-5 h-5 text-cyan-400" />
                  <span className="font-medium text-slate-200">Windows & Doors</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-slate-400">
                    {windows.count || 0} windows, {doors.count || 0} doors
                  </span>
                  {expandedSections.windows ? (
                    <ChevronUp className="w-4 h-4 text-slate-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                  )}
                </div>
              </button>
              
              {expandedSections.windows && (
                <div className="p-3 border-t border-slate-700 bg-slate-800/20 text-sm text-slate-400">
                  <p>Windows: {windows.count || 0}</p>
                  <p>Doors: {doors.count || 0}</p>
                </div>
              )}
            </div>
          )}

          {/* Gutters & Trim Section */}
          {(gutters.linear_feet > 0 || Object.keys(trim).length > 0) && (
            <div className="border border-slate-700 rounded overflow-hidden">
              <button
                onClick={() => toggleSection('gutters')}
                className="w-full p-3 flex items-center justify-between hover:bg-slate-800/50 bg-slate-800/30"
              >
                <div className="flex items-center gap-3">
                  <Droplets className="w-5 h-5 text-green-400" />
                  <span className="font-medium text-slate-200">Gutters & Trim</span>
                </div>
                <div className="flex items-center gap-3">
                  {expandedSections.gutters ? (
                    <ChevronUp className="w-4 h-4 text-slate-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                  )}
                </div>
              </button>
              
              {expandedSections.gutters && (
                <div className="p-3 border-t border-slate-700 bg-slate-800/20 text-sm text-slate-400 space-y-1">
                  {gutters.linear_feet > 0 && (
                    <p>Gutters: {gutters.linear_feet} LF</p>
                  )}
                  {trim.fascia_lf > 0 && (
                    <p>Fascia: {trim.fascia_lf} LF</p>
                  )}
                  {trim.soffit_sf > 0 && (
                    <p>Soffit: {formatSF(trim.soffit_sf)} SF</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-4 bg-slate-800/50 border-t border-slate-700 flex items-center justify-between">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-slate-400 hover:text-slate-200"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmImport}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded
              flex items-center gap-2"
            data-testid="hover-confirm-button"
          >
            <Check className="w-4 h-4" />
            Import Exterior Data
          </button>
        </div>
      </div>
    );
  }

  if (status === 'confirming') {
    return (
      <div className="p-6 border-2 border-slate-700 rounded-lg bg-slate-800/30">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-500 mx-auto mb-3 animate-spin" />
          <h4 className="text-lg font-medium text-slate-200 mb-2">Importing data...</h4>
          <p className="text-sm text-slate-400">
            Adding exterior measurements to your field visit
          </p>
        </div>
      </div>
    );
  }

  if (status === 'complete') {
    return (
      <div className="p-6 border-2 border-blue-500/30 rounded-lg bg-blue-500/5">
        <div className="text-center">
          <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
            <Check className="w-6 h-6 text-blue-400" />
          </div>
          <h4 className="text-lg font-medium text-blue-400 mb-2">Import Complete!</h4>
          <p className="text-sm text-slate-400 mb-4">
            Exterior measurements have been added to your field visit.
          </p>
          <button
            onClick={handleCancel}
            className="text-sm text-blue-400 hover:text-blue-300"
          >
            Import another report
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default HoverUpload;
